"use client";

import * as React from "react";
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";
import { cn } from "lib/utils";

/* Root */
function Dropdown(props: React.ComponentProps<typeof DropdownPrimitive.Root>) {
  return <DropdownPrimitive.Root data-slot="dropdown" {...props} />;
}

function DropdownTrigger(
  props: React.ComponentProps<typeof DropdownPrimitive.Trigger>
) {
  return <DropdownPrimitive.Trigger data-slot="dropdown-trigger" {...props} />;
}

function DropdownContent({
  className,
  sideOffset = 6,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.Content>) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          // themed card-like surface — token driven, one definition per theme
          "bg-surface-elevated text-content border border-line",
          /*
           * Above the app chrome. The menu is portalled to <body>, so it sits
           * in the same stacking context as the sidebar (z-60) and any open
           * dialog or drawer (z-110). At the old z-50 the sidebar painted over
           * it, which is why the account menu appeared cut off along the rail.
           * 115 clears both and still sits below the command palette (z-120).
           */
          "rounded-xl shadow-elevated p-1 z-[115]",
          /*
           * Height follows the space Radix measured between the trigger and the
           * viewport edge, so a menu shows all of itself whenever it fits.
           *
           * This was a flat `max-h-[300px]` paired with `no-scrollbar`: the
           * account menu is ~450px, so it was cut off after "Developer
           * Settings" — and because the scrollbar was hidden there was nothing
           * to suggest Support, Help & Docs and Log out were still down there.
           * A scrollable box with no scrollbar reads as a box with missing
           * content.
           */
          "max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto overscroll-contain",
          // Radix animations
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=top]:slide-in-from-bottom-2",
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          className
        )}
        {...props}
      />
    </DropdownPrimitive.Portal>
  );
}

function DropdownItem({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.Item> & {
  inset?: boolean;
  variant?: "default" | "destructive";
}) {
  return (
    <DropdownPrimitive.Item
      className={cn(
        "relative flex select-none items-center gap-2 rounded-lg px-2 py-2 text-ui outline-none",
        "cursor-pointer transition-colors duration-150",
        "text-content-secondary",
        "focus:bg-brand-subtle focus:text-brand-on-subtle",
        "hover:bg-surface-hover hover:text-content",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        inset && "pl-8",
        variant === "destructive" &&
          "text-danger focus:bg-danger-subtle focus:text-danger hover:bg-danger-subtle hover:text-danger",
        className
      )}
      {...props}
    />
  );
}

function DropdownCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.CheckboxItem>) {
  return (
    <DropdownPrimitive.CheckboxItem
      className={cn(
        "relative flex select-none items-center rounded-lg py-2 pr-2 pl-8 text-ui outline-none",
        "cursor-pointer transition-colors duration-150",
        "text-content-secondary",
        "focus:bg-brand-subtle focus:text-brand-on-subtle",
        "hover:bg-surface-hover hover:text-content",
        "data-[state=checked]:text-content data-[state=checked]:font-semibold",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center text-brand">
        <DropdownPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </DropdownPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownPrimitive.CheckboxItem>
  );
}

function DropdownRadioGroup(
  props: React.ComponentProps<typeof DropdownPrimitive.RadioGroup>
) {
  return <DropdownPrimitive.RadioGroup {...props} />;
}

function DropdownRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.RadioItem>) {
  return (
    <DropdownPrimitive.RadioItem
      className={cn(
        "relative flex select-none items-center rounded-lg py-2 pr-2 pl-8 text-ui outline-none",
        "cursor-pointer transition-colors duration-150",
        "text-content-secondary",
        "focus:bg-brand-subtle focus:text-brand-on-subtle",
        "hover:bg-surface-hover hover:text-content",
        "data-[state=checked]:text-content data-[state=checked]:font-semibold",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex size-3.5 items-center justify-center text-brand">
        <DropdownPrimitive.ItemIndicator>
          <CircleIcon className="size-2 fill-current" />
        </DropdownPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownPrimitive.RadioItem>
  );
}

function DropdownLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.Label> & {
  inset?: boolean;
}) {
  return (
    <DropdownPrimitive.Label
      className={cn(
        "px-2 pt-2 pb-1 text-2xs font-bold uppercase tracking-wider text-content-subtle",
        inset && "pl-8",
        className
      )}
      {...props}
    />
  );
}

function DropdownSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.Separator>) {
  return (
    <DropdownPrimitive.Separator
      className={cn("my-1 h-px bg-line", className)}
      {...props}
    />
  );
}

function DropdownSub(
  props: React.ComponentProps<typeof DropdownPrimitive.Sub>
) {
  return <DropdownPrimitive.Sub {...props} />;
}

function DropdownSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.SubTrigger> & {
  inset?: boolean;
}) {
  return (
    <DropdownPrimitive.SubTrigger
      className={cn(
        "flex items-center rounded-lg px-2 py-2 text-ui outline-none text-content-secondary",
        "cursor-pointer transition-colors duration-150",
        "focus:bg-brand-subtle focus:text-brand-on-subtle",
        "hover:bg-surface-hover hover:text-content",
        inset && "pl-8",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4 opacity-70" />
    </DropdownPrimitive.SubTrigger>
  );
}

function DropdownSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownPrimitive.SubContent>) {
  return (
    <DropdownPrimitive.SubContent
      className={cn(
        "bg-surface-elevated text-content border border-line",
        // Same layer as the parent menu — see DropdownContent above.
        "rounded-xl shadow-elevated p-1 z-[115]",
        className
      )}
      {...props}
    />
  );
}

export {
  Dropdown,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownCheckboxItem,
  DropdownRadioGroup,
  DropdownRadioItem,
  DropdownLabel,
  DropdownSeparator,
  DropdownSub,
  DropdownSubTrigger,
  DropdownSubContent,
};
